import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Ajv from "ajv";
import { Col, Container, Form, Image, InputGroup, LineSeparator, Row, SpinnerBackdrop } from "banafo-design-system";
import { PAYMENT_CUSTOMER_TYPE_COMPANY, PAYMENT_CUSTOMER_TYPE_PRIVATE, PAYMENT_METHOD_VISA } from "src/features/payments/config";
import { useCurrentPlan } from "src/features/auth/hooks/useCurrentPlan";
import { selectReviewModalIsOpen, showReviewModal, useCancelPaymentMutation, useGetBillingCountriesQuery, useGetCurrentPlanQuery, useLazyGetCurrentPlanQuery, useLazyGetVatNumberQuery } from "src/features/payments/paymentsSlice";
import validateSchema from "src/features/auth/util/validateSchema";
import { markI18nKey } from "src/features/auth/util/markI18nKey";
import BillingConfirmModal from "src/features/payments/billing/BillingConfirmModal";
import { selectAuthUser, selectAuthUserPlan } from "src/features/auth/store/selectors/auth";
import PlanItem from "src/features/account/pricingPlans/PlanItem";
import { SUBSCRIPTION_PERIOD_MONTHLY, SUBSCRIPTION_PERIOD_YEARLY, SUBSCRIPTION_PLAN_PREMIUM_YEARLY, UPGRADE_PLAN } from "src/features/account/config";
import { cancelPaymentSuccess } from "src/features/auth/store/actions/payment";
import logo from "./images/logo-dark.svg";
import card_issue from "./images/card_issue.jpg";
import { HOME_ROUTE, WIZARD_ROUTE } from "src/app/router/config";
import ProPlanFeatures from "src/features/account/pricingPlans/features/ProPlanFeatures";
import { Button } from "@mantine/core";
const ajv = new Ajv({ allErrors: true, jsonPointers: true, coerceTypes: true });
require("ajv-errors")(ajv);
const schema = {
    type: "object",
    required: ["email", "full_name", "country", "customerType"],
    properties: {
        email: {
            type: "string",
            format: "email",
            minLength: 3,
            maxLength: 32,
        },
        full_name: {
            type: "string",
            minLength: 1,
        },
        country: {
            type: "string",
            minLength: 1,
        },
        customerType: {
            type: ["string"],
            minLength: 3,
            maxLength: 128,
        },
    },
    allOf: [
        {
            if: {
                properties: {
                    customerType: {
                        const: "company",
                    },
                },
            },
            then: {
                required: ["company_name"],
                properties: {
                    vat_number: {
                        type: "string",
                        pattern: "^[A-Za-z0-9_-]*$",
                    },
                    company_name: {
                        minLength: 1,
                    },
                },
            },
        },
    ],
    errorMessage: {
        properties: {
            credits: markI18nKey("errors:Invalid.credits_"),
            email: markI18nKey("errors:Invalid.email_"),
            full_name: markI18nKey("errors:Invalid.full.name_"),
            country: markI18nKey("errors:Select.a.valid.country_"),
            vat_number: markI18nKey("errors:Invalid.VAT.number_"),
            company_name: markI18nKey("errors:Company.name.is.required_"),
        },
    },
};
const BillingPage = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const params = useParams();
    useCurrentPlan(params.plan);
    const userPlan = useSelector((state) => selectAuthUserPlan(state));
    const authUser = useSelector(selectAuthUser);
    const initialFormState = {
        paymentMethod: PAYMENT_METHOD_VISA,
        email: "",
        full_name: "",
        country: "",
        customerType: PAYMENT_CUSTOMER_TYPE_PRIVATE,
        vat_number: "",
        company_name: "",
        company_address: "",
        userPlan: userPlan.key,
        subtotal: null,
        billingPeriod: "",
    };
    const navigate = useNavigate();
    const [inputs, setInputs] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [yearly, setYearly] = useState(params.plan === SUBSCRIPTION_PLAN_PREMIUM_YEARLY ? true : false);
    const isOpenReviewModal = useSelector(selectReviewModalIsOpen);
    const [getVatNumber, { data, isFetching, isSuccess }, lastPromiseInfo] = useLazyGetVatNumberQuery();
    const { data: { list: countries = [] } = {}, isSuccess: isSuccessCountries } = useGetBillingCountriesQuery();
    const { isError: isErrorGetCurrentPlan } = useGetCurrentPlanQuery(params.plan);
    const [getCurrentPlan, { isSuccess: isSuccessCurrentPlan }] = useLazyGetCurrentPlanQuery();
    const [cancelPayment] = useCancelPaymentMutation();
    useEffect(() => {
        if (!isSuccess) {
            return;
        }
        const { lastArg: { country, vatNumber }, } = lastPromiseInfo;
        const { isValidVATNumber } = data;
        if (inputs.customerType === PAYMENT_CUSTOMER_TYPE_COMPANY &&
            vatNumber &&
            !isValidVATNumber) {
            setErrors({ vat_number: t("errors:Invalid.VAT.number_") });
            return;
        }
        dispatch(showReviewModal());
    }, [isFetching, isSuccess]);
    useEffect(() => {
        setInputs((inputs) => (Object.assign(Object.assign({}, inputs), { ["subtotal"]: userPlan.initialAmount, ["billingPeriod"]: userPlan.billingPeriod, ["userPlan"]: userPlan.key })));
    }, [isSuccessCurrentPlan, userPlan]);
    const handleSetYearly = (e) => {
        setYearly(e.target.checked);
        getCurrentPlan(e.target.checked ? SUBSCRIPTION_PLAN_PREMIUM_YEARLY : params.plan);
    };
    const handleInputChange = (e) => {
        setInputs((inputs) => (Object.assign(Object.assign({}, inputs), { [e.target.name]: e.target.value })));
    };
    const renderCountries = () => {
        if (!isSuccessCountries) {
            return null;
        }
        return countries.map(({ iso, name }) => (_jsx("option", { value: iso, children: name }, iso)));
    };
    const handleSubmitForm = () => {
        setErrors({});
        const { hasErrors, errors } = validateSchema(ajv, schema, inputs);
        if (hasErrors) {
            setErrors(errors);
            return;
        }
        const { country, customerType, vat_number } = inputs;
        const vatNumber = customerType === PAYMENT_CUSTOMER_TYPE_COMPANY ? vat_number : "";
        setInputs((inputs) => (Object.assign(Object.assign({}, inputs), { ["subtotal"]: userPlan.initialAmount, ["billingPeriod"]: userPlan.billingPeriod })));
        getVatNumber({ country, vatNumber });
    };
    const handleCancelPayment = () => {
        cancelPayment({}).unwrap()
            .then((response) => {
            dispatch(cancelPaymentSuccess(response));
            if (authUser.showWizard) {
                navigate(WIZARD_ROUTE);
            }
            navigate(HOME_ROUTE);
        })
            .catch((e) => Sentry.captureException(e));
    };
    if (isErrorGetCurrentPlan) {
        return (_jsx(Container, { children: _jsx(Row, { className: "justify-content-center full-width", children: _jsxs(Col, { style: { maxWidth: 800 }, children: [_jsxs("div", { className: "text-center py-200 pt-lg-400 pb-lg-200 m-auto", children: [_jsx(Image, { fluid: true, src: card_issue }), _jsxs("h1", { className: "text-uppercase fw-normal mb-300", children: ["- ", t('Payment.issue_'), "! -"] }), _jsx("h6", { className: "text-uppercase fw-bold", children: t('infos:Payment.processing.issue_') })] }), _jsx(LineSeparator, { color: "secondary" }), _jsx("div", { className: "py-200 px-400 px-lg-800 text-center", children: _jsx(Button, { variant: "dark", type: "submit", className: "text-uppercase mb-100 px-400", onClick: () => navigate('/'), children: t("Continue_") }) })] }) }) }));
    }
    return (_jsxs(_Fragment, { children: [isOpenReviewModal && _jsx(BillingConfirmModal, Object.assign({}, inputs)), isFetching && _jsx(SpinnerBackdrop, {}), _jsx(Container, { children: _jsx(Row, { children: _jsx(Col, { xs: 12, children: _jsxs(Form, { className: "text-center mb-300 mt-200", children: [_jsx("div", { className: "position-absolute d-none d-lg-block", children: _jsx("img", { src: logo, height: 60, alt: "banafo logo" }) }), _jsx(Row, { className: "justify-content-center mt-200", children: _jsxs(Col, { xs: 12, sm: 5, children: [_jsx(PlanItem, { plan: !yearly ? UPGRADE_PLAN : SUBSCRIPTION_PLAN_PREMIUM_YEARLY, active: true, subscriptionPeriod: yearly ? SUBSCRIPTION_PERIOD_YEARLY : SUBSCRIPTION_PERIOD_MONTHLY }), _jsx(ProPlanFeatures, {})] }) }), _jsxs(Row, { className: "justify-content-center mt-200", children: [_jsxs(Col, { xs: 12, lg: 4, children: [params.upgrade && params.upgrade === "upgrade" && (_jsx(Form.Group, { controlId: "customerInfo", as: Col, xs: "12", children: _jsxs("div", { className: "text-center mb-200", children: [_jsx("label", { className: "me-50", role: "button", htmlFor: "switch-month-anual-plan", children: t("Monthly_") }), _jsx("strong", { children: _jsx(Form.Check, { id: "switch-month-anual-plan", label: t("Yearly.save.20%_"), checked: yearly, onChange: handleSetYearly, type: "switch", inline: true }) })] }) })), _jsxs(Form.Group, { controlId: "customerInfo", as: Col, xs: "12", children: [_jsx("h2", { children: _jsx(Form.Label, { children: t("Customer.info_") }) }), _jsx(Form.Control, { placeholder: t("Email_"), name: "email", onChange: handleInputChange, value: inputs.email, isInvalid: !!errors.email, className: "mb-100 TeamsAndMembers-filter" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.email }), _jsx(Form.Control, { placeholder: t("Full.name_"), name: "full_name", onChange: handleInputChange, value: inputs.full_name, isInvalid: !!errors.full_name, className: "mb-100 TeamsAndMembers-filter" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.full_name }), _jsxs(Form.Select, { placeholder: t("Country_"), name: "country", onChange: handleInputChange, value: inputs.country, isInvalid: !!errors.country, className: "mb-150", children: [_jsx("option", { value: "", children: t("Select.a.country_") }), renderCountries()] }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.country })] }), _jsxs(Form.Group, { controlId: "customerType", children: [_jsxs(Form.Check, { variant: "tertiary", custom: true, inline: true, type: "radio", id: "private", children: [_jsx(Form.Check.Input, { name: "customerType", value: "private", onChange: handleInputChange, checked: "private" === inputs.customerType }), _jsx(Form.Check.Label, { children: t("Private.customer_") })] }, "private"), _jsxs(Form.Check, { variant: "tertiary", custom: true, inline: true, type: "radio", id: "company", children: [_jsx(Form.Check.Input, { name: "customerType", value: "company", onChange: handleInputChange, checked: "company" === inputs.customerType }), _jsx(Form.Check.Label, { children: t("Company_") })] }, "company"), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.customerType })] }), inputs.customerType === "company" && (_jsx(_Fragment, { children: _jsxs(Form.Group, { controlId: "companyInfo", as: Col, xs: "12", children: [_jsxs(InputGroup, { className: "mb-50 mt-150", children: [_jsx(InputGroup.Text, { className: "rounded-right-0 btn btn-dark", children: inputs.country }), _jsx(Form.Control, { placeholder: t("Company.VAT.number_"), name: "vat_number", onChange: handleInputChange, value: inputs.vat_number, isInvalid: !!errors.vat_number, className: "TeamsAndMembers-filter rounded-end" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.vat_number })] }), _jsxs("small", { className: "text-muted", children: ["*", t("infos:Vat.number.without.country.code_")] }), _jsx(Form.Control, { placeholder: t("Company.name_"), name: "company_name", onChange: handleInputChange, value: inputs.company_name, isInvalid: !!errors.company_name, className: "mb-100 TeamsAndMembers-filter" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.company_name }), _jsx(Form.Control, { placeholder: t("Company.address_"), name: "company_address", onChange: handleInputChange, value: inputs.companyAddress, isInvalid: !!errors.company_address, className: "TeamsAndMembers-filter" })] }) }))] }), _jsx(Col, { xs: 12, lg: 10, children: _jsx(LineSeparator, { color: "secondary", className: "my-300" }) }), _jsx(Col, { xs: 12, lg: 8, xl: 6, className: "mb-100", children: _jsxs("div", { className: "d-flex flex-wrap gap-100", children: [_jsx(Button, { variant: "outline-filled", color: "dark", tt: "uppercase", onClick: handleCancelPayment, fullWidth: true, style: { flex: '1 1 270px' }, children: t("Continue.with.a.free.plan_") }), _jsx(Button, { variant: "filled", color: "dark", tt: "uppercase", onClick: handleSubmitForm, fullWidth: true, style: { flex: '1 1 270px' }, children: t("Checkout_") })] }) })] })] }) }) }) })] }));
};
export default BillingPage;
